'use client';

/**
 * The order tiles of the workspace with what their buttons do:
 *  - «Нарисовать персонажей» / «Нарисовать страницы» — an add-on order with its
 *    own estimate: confirm dialog → placeAddOn → the run waiting on btn1 / btn2
 *    goes on. A layer without a v2 run (order made by the v1 executor) falls
 *    back to the v1 action of that part.
 *  - «Выбрать другой голос» — the narration waits on its voice: voice picker →
 *    chooseNarrationVoice.
 */
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { VoiceSelector } from '@/contexts/stories/components/VoiceSelector';
import { estimateAddOn, placeAddOn } from '../actions/addOnOrder';
import { chooseNarrationVoice } from '../actions/chooseVoice';
import { pipelineKeys } from '../queryKeys';
import type { AddOnPart, OrderLayersView } from '../order/types';
import { OrderLayerTiles } from './OrderLayerTiles';

type Layer = OrderLayersView['layers'][number];

export interface OrderLayerPanelProps {
  storyId: string;
  order: OrderLayersView | null | undefined;
  /** v1 actions for a layer that has no v2 run yet */
  fallback?: Partial<Record<AddOnPart, () => void>>;
  familyId?: string;
}

export function OrderLayerPanel({ storyId, order, fallback, familyId }: OrderLayerPanelProps) {
  const t = useTranslations('stories.orderLayers');
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<{ part: AddOnPart; credits: number } | null>(null);
  const [voiceFor, setVoiceFor] = useState<Layer | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: pipelineKeys.orderLayers(storyId) });

  const ask = async (part: AddOnPart) => {
    setError(null);
    setPending(true);
    try {
      const est = await estimateAddOn(storyId, part);
      if (!est.success) {
        if (fallback?.[part]) fallback[part]!();
        else setError(t('addOn.error'));
        return;
      }
      setConfirm({ part, credits: est.data.credits });
    } finally {
      setPending(false);
    }
  };

  const place = async () => {
    if (!confirm) return;
    const { part } = confirm;
    setPending(true);
    try {
      const res = await placeAddOn(storyId, part);
      setConfirm(null);
      if (!res.success) {
        if (res.code === 'ADDON_NO_RUN' && fallback?.[part]) fallback[part]!();
        else setError(t('addOn.error'));
      }
      await refresh();
    } finally {
      setPending(false);
    }
  };

  const pickVoice = async (voiceId: string) => {
    if (!voiceFor) return;
    setPending(true);
    setError(null);
    try {
      const res = await chooseNarrationVoice({ storyId, layerId: voiceFor.layerId, voiceId });
      if (!res.success) { setError(res.code === 'VOICE_REFUSED' ? t('voice.refused') : t('voice.error')); return; }
      setVoiceFor(null);
      await refresh();
    } finally {
      setPending(false);
    }
  };

  const onAddOn: Partial<Record<AddOnPart, () => void>> = {
    characters: () => void ask('characters'),
    pages: () => void ask('pages'),
  };
  const language = typeof voiceFor?.params.language === 'string' ? voiceFor.params.language : undefined;

  return (
    <>
      <OrderLayerTiles order={order} onAddOn={onAddOn} onChooseVoice={setVoiceFor} pending={pending} />
      {error && <p className="text-xs text-destructive" data-testid="order-layers-error">{error}</p>}

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent data-testid="addon-confirm-dialog" data-part={confirm?.part ?? ''}>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm ? t(confirm.part === 'characters' ? 'drawCharacters' : 'drawPages') : ''}</AlertDialogTitle>
            <AlertDialogDescription data-testid="addon-confirm-credits">
              {confirm ? t('addOn.estimate', { credits: confirm.credits }) : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending} data-testid="addon-confirm-cancel">{t('addOn.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={pending}
              data-testid="addon-confirm-apply"
              onClick={(e) => { e.preventDefault(); void place(); }}
            >
              {t('addOn.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!voiceFor} onOpenChange={(o) => { if (!o) setVoiceFor(null); }}>
        <AlertDialogContent data-testid="choose-voice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('chooseVoice')}</AlertDialogTitle>
            <AlertDialogDescription>{t('voice.hint')}</AlertDialogDescription>
          </AlertDialogHeader>
          <VoiceSelector
            familyId={familyId}
            language={language as never}
            disabled={pending}
            onVoiceSelect={(voiceId) => void pickVoice(voiceId)}
          />
          {error && <p className="text-xs text-destructive" data-testid="choose-voice-error">{error}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>{t('addOn.cancel')}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
